// imports section Start
import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  TextField,
  Button,
  Pagination,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import GuestCategories from "../../components/guest/GuestCategories";
import GuestItemList from "../../components/guest/GuestItemList";
import GuestItemGrid from "../../components/guest/GuestItemGrid";
import GuestSlider from "../../components/guest/GuestSlider";
import GuestBottomNavigation from "../../components/guest/GuestBottomNavigation";
import { useStyles } from "../../assets/styles/Theme";
import { IMAGEBASEURL } from "../../config/Config";
import { externalgetApi } from "../../services/external/externalApis";
import {
  COMMON_API_02,
  SETTINGS_API_16,
  SETTINGS_API_21,
} from "../../api/ApiModel";
import { addGuestItemToCart } from "../../store/ReduxGuestCartReducer";
import { setUserParams } from "../../store/ReduxUser";
import {
  CON_STATUS_ACTIVE,
  CON_MENU_TYPE_CODE,
  CON_VALUE_ZERO,
} from "../../utils/sys/Constants";
import { encodeUrl, decodeUrl } from "../../utils/common/UrlUtils";
import { setGuestDecoded } from "../../store/ReduxGuestDecodedReducer";
import SnackbarMessage from "../../components/common/SnackbarMessage";
import { useLocation } from "react-router-dom";
// imports section End

export default function Guest() {
  // Hooks start
  const dispatch = useDispatch();
  const theme = useTheme();
  const styles = useStyles(theme);
  const tabsRef = useRef(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [value, setValue] = useState("All");
  const [categoryName, setCategoryName] = useState("All");
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [promotionalMenu, setPromotionalMenu] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  const [isGridView, setIsGridView] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const location = useLocation();

  useEffect(() => {
    // If there is a snackbar message from the previous navigation state, open the Snackbar
    if (location.state?.snackbarMessage) {
      setSnackbarMessage(location.state.snackbarMessage);
      setSnackbarSeverity(location.state.snackbarSeverity || "success");
      setSnackbarOpen(true);
    }
  }, [location.state]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);

  // Extract query parameters from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedCompcode = urlParams.get("compcode");
    const encodedLoccode = urlParams.get("loccode");
    const encodedRestcode = urlParams.get("restcode");
    const encodedTablecode = urlParams.get("tablecode") || btoa(CON_VALUE_ZERO);

    // Decode parameters
    const compcode = decodeUrl(encodedCompcode);
    const loccode = decodeUrl(encodedLoccode);
    const restcode = decodeUrl(encodedRestcode);
    const tablecode = decodeUrl(encodedTablecode);

    console.log("Decoded Compcode:", compcode);
    console.log("Decoded Loccode:", loccode);
    console.log("Decoded Restcode:", restcode);
    console.log("Decoded tablecode:", tablecode);

    // Dispatch to Redux
    dispatch(
      setGuestDecoded({
        COMPCODE: compcode,
        LOCCODE: loccode,
        RESTCODE: restcode,
        TABLECODE: tablecode,
      })
    );

    // Fetch categories and items
    fetchData(compcode, loccode, restcode, tablecode);
    fetchPromotionalMenu(compcode, loccode, restcode);
  }, [dispatch]);

  useEffect(() => {
    filterItems(value);
  }, [searchQuery, value]);

  // Get user parameters from Redux store
  // const SYS_USER = useSelector((state) => state.user) || {};
  // const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};
  const guestDecoded = useSelector((state) => state.guestDecoded);

  // Extract query parameters from Redux state or fallback to empty values
  const [queryParams, setQueryParams] = useState({
    compcode: "",
    loccode: "",
    restcode: "",
    tablecode: "",
  });

  useEffect(() => {
    if (guestDecoded) {
      setQueryParams({
        compcode: guestDecoded.COMPCODE || "",
        loccode: guestDecoded.LOCCODE || "",
        restcode: guestDecoded.RESTCODE || "",
        tablecode: guestDecoded.TABLECODE || "",
      });
    }
  }, [guestDecoded]);

  // Handlers start
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Paginate items
  const itemsPerPage = 20;
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleChange = (event, newValue) => {
    setValue(newValue);
    filterItems(newValue);

    // Update category name
    const selectedCategory = categories.find(
      (category) => category.MENUCATCODE === newValue
    );
    setCategoryName(selectedCategory ? selectedCategory.MENUCATNAME : "All");

    // Scroll to the selected tab
    const tabElement = tabsRef.current?.querySelector(
      `button[value="${newValue}"]`
    );
    tabElement?.scrollIntoView({ behavior: "smooth", inline: "center" });
  };

  const filterItems = (categoryCode) => {
    let items =
      categoryCode === "All"
        ? allItems
        : allItems.filter((item) => item.MENUCATCODE === categoryCode);

    if (searchQuery) {
      items = items.filter((item) =>
        item.MENUNAME.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredItems(items);
  };

  const handleCategoryDialogOpen = () => setCategoryDialogOpen(true);
  const handleCategoryDialogClose = () => setCategoryDialogOpen(false);

  const handleSelectCategory = (category) => {
    setValue(category.MENUCATCODE);
    setCategoryName(category.MENUCATNAME);
    filterItems(category.MENUCATCODE);
    setCategoryDialogOpen(false);

    // Scroll to the selected category
    const categoryElement = tabsRef.current?.querySelector(
      `button[value="${category.MENUCATCODE}"]`
    );
    categoryElement?.scrollIntoView({ behavior: "smooth", inline: "center" });
  };

  const handleViewOrder = (order) => setSelectedOrder(order);

  const sortedCategories = categories
    .filter((category) => category.MENUCATCODE !== "All")
    .sort((a, b) => a.MENUCATNAME.localeCompare(b.MENUCATNAME));

  const uniqueCategories = [
    { MENUCATCODE: "All", MENUCATNAME: "All" },
    ...dynamicCategories,
    ...sortedCategories,
  ].reduce((acc, category) => {
    if (!acc.some((cat) => cat.MENUCATCODE === category.MENUCATCODE)) {
      acc.push(category);
    }
    return acc;
  }, []);

  const addToCart = (item) => {
    dispatch(addGuestItemToCart(item));
  };

  const handleCartOpen = () => {
    setCartOpen(true);
  };

  const handleCartClose = () => {
    setCartOpen(false);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  // Handlers end

  // API calls start
  const fetchData = async (compcode, loccode, restcode) => {
    try {
      const params = {
        COMPCODE: compcode,
        LOCCODE: loccode,
        RESTCODE: restcode,
        MENUTYPECODE: CON_MENU_TYPE_CODE,
        STS: CON_STATUS_ACTIVE,
      };
      const categoryResponse = await externalgetApi(
        COMMON_API_02,
        "GET",
        params
      );
      setCategories(categoryResponse);
      fetchAllItems(compcode, loccode, restcode);
    } catch (error) {
      setError(error);
    }
  };

  const fetchAllItems = async (compcode, loccode, restcode) => {
    try {
      const params = {
        COMPCODE: compcode,
        LOCCODE: loccode,
        RESTCODE: restcode,
        STS: CON_STATUS_ACTIVE,
      };
      const itemsResponse = await externalgetApi(COMMON_API_02, "GET", params);
      setAllItems(itemsResponse);
      setFilteredItems(itemsResponse);
    } catch (error) {
      setError(error);
    }
  };

  const fetchItems = async (categoryCode, query) => {
    try {
      const params = {
        COMPCODE: guestDecoded.COMPCODE, // Use guestDecoded
        LOCCODE: guestDecoded.LOCCODE, // Use guestDecoded
        RESTCODE: guestDecoded.RESTCODE,
        MENUCATCODE: categoryCode,
        STS: CON_STATUS_ACTIVE,
      };
      const itemsResponse = await externalgetApi(COMMON_API_02, "GET", params);
      const filteredItems = itemsResponse.filter((item) =>
        item.MENUNAME.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredItems(filteredItems);
      setAllItems(itemsResponse);
    } catch (error) {
      setError(error);
    }
  };

  const fetchPromotionalMenu = async (compcode, loccode, restcode) => {
    try {
      const params = {
        COMPCODE: compcode,
        LOCCODE: loccode,
        RESTCODE: restcode,
        SCHEDULEDATE: new Date().toISOString().split("T")[0],
        STS: CON_STATUS_ACTIVE,
      };

      const promotionalResponse = await externalgetApi(
        SETTINGS_API_21,
        "GET",
        params
      );
      console.log("promotionalResponse", promotionalResponse);
      setPromotionalMenu(promotionalResponse);
      // fetchAllItems(compcode, loccode, restcode);
    } catch (error) {
      setError(error);
    }
  };
  // API calls end

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: theme.palette.background.paper }}>
      <GuestSlider
        promotionalMenu={promotionalMenu}
        IMAGEBASEURL={IMAGEBASEURL}
      />
      <Box
        sx={{
          mt: 1,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: {
              xl: "40%",
              lg: "40%",
              md: "60%",
              sm: "100%",
              xs: "100%",
            },
          }}
        >
          <TextField
            id="outlined-search"
            placeholder="Search Items"
            type="search"
            size="small"
            fullWidth
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              color: theme.palette.text.buttontxt,
              height: "3em",
            }}
            value={searchQuery}
            onClick={handleSearchChange}
          >
            Search
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          position: "sticky",
          pt: 2,
          top: 50,
          zIndex: 1000,
          backgroundColor: theme.palette.background.paper,
          boxShadow: 1,
        }}
      >
        <GuestCategories
          ref={tabsRef}
          categories={uniqueCategories}
          onSelectCategory={handleSelectCategory}
          selectedCategory={value} // Pass the selected category to highlight it
          onClose={handleCartClose}
          existingCategories={categories}
        />
        <Box sx={styles.guest_item_title}>
          <Typography variant="title"> {categoryName}</Typography>
          <IconButton onClick={() => setIsGridView(!isGridView)}>
            {isGridView ? <ViewListIcon /> : <ViewModuleIcon />}
          </IconButton>
        </Box>
      </Box>

      <Box sx={styles.cards_container} paddingX={4} paddingY={2}>
        <Grid container spacing={4} paddingX={2}>
          {paginatedItems.length > 0 ? (
            <>
              {paginatedItems.map((item, index) => (
                <Grid
                  item
                  xs={isGridView ? 12 : 12}
                  sm={isGridView ? 12 : 12}
                  md={isGridView ? 6 : 6}
                  lg={isGridView ? 3 : 6}
                  xl={isGridView ? 3 : 6}
                  key={index}
                >
                  {isGridView ? (
                    <GuestItemGrid
                      addToCart={addToCart}
                      cartItem={item}
                      IMAGEBASEURL={IMAGEBASEURL}
                    />
                  ) : (
                    <GuestItemList
                      addToCart={addToCart}
                      cartItem={item}
                      IMAGEBASEURL={IMAGEBASEURL}
                    />
                  )}
                </Grid>
              ))}
            </>
          ) : (
            <CircularProgress />
          )}
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error.message}</Alert>
            </Grid>
          )}
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={Math.ceil(filteredItems.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      </Box>
      <GuestBottomNavigation openCart={handleCartOpen} />
      {snackbarMessage && (
        <SnackbarMessage
          open={snackbarOpen} // Control the Snackbar visibility
          handleClose={handleCloseSnackbar} // Close handler
          message={snackbarMessage} // Message from location state
          severity={snackbarSeverity} // Dynamic severity (success or error)
        />
      )}
    </Box>
  );
}
